import {
	C_SLIP_MULTIPLIED_CO2EQ_TTW_SLIP,
	CO2EQ_TTW_SLIP,
	CSF_CH4_SLIP,
	CSF_CO2_SLIP,
	CSF_N2O_SLIP,
	EMISSION_NORMALIZATION_FACTOR,
	ENERGY_PER_TONNE_OF_FUEL,
	FuelClass,
	FuelEuData,
	FuelEuPenlaties,
	FuelTypes,
	GWP_CH4,
	GWP_CO2,
	GWP_N2O,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatNumber } from '@shared/utils/formatNumber';
import type { FuelEuProps } from '@api/models/fuel-eu';

export type ValueUnit = {
	label: Values<typeof FuelDataCalculationLabel>;
	value: number;
	displayValue: string | null;
	unit: Values<typeof UnitTypes>;
};

export const UnitTypes = {
	G_CO2_EQ_PER_MJ: 'gCO2eq / MJ',
	MJ_PER_G_FUEL: 'MJ / gFuel',
	G_CO2_EQ_PER_G_FUEL: 'gCO2eq / gFuel',
	G_CO2_PER_G_FUEL: 'gCO2 / gFuel',
	G_CH4_PER_G_FUEL: 'gCH4 / gFuel',
	G_N2O_PER_G_FUEL: 'gN2O / gFuel',
	PERCENT: '%',
	G_CO2_EQ: 'gCO2eq',
	MJ: 'MJ',
	NONE: '',
} as const;

export type FuelEuDataCalculation = {
	id: number;
	estimateId: number;
	name: string;
	fuelType: Values<typeof FuelTypes>;
	consumption: number;
	year: number;
	wellToTank: {
		co2eqWtt: ValueUnit;
		lcv: ValueUnit;
		rwd: ValueUnit;
	};
	tankToWake: {
		co2eqTtw: ValueUnit;
		cfCo2: ValueUnit;
		cfCh4: ValueUnit;
		cfN2o: ValueUnit;
		slipFactor: ValueUnit;
	};
	cSlip: {
		co2eqTtwSlip: ValueUnit;
		csfCo2: ValueUnit;
		csfCh4: ValueUnit;
		csfN2o: ValueUnit;
		cSlip: ValueUnit;
		cSlipCo2eqTtwSlip: ValueUnit;
		ttwTotalInclSlip: ValueUnit;
	};
	parameters: {
		wellToTankShip: ValueUnit;
		tankToWakeShip: ValueUnit;
		co2eqEmittedWtT: ValueUnit;
		co2eqEmittedTtW: ValueUnit;
		co2eqEmittedWtW: ValueUnit;
		energyUsedOnBoard: ValueUnit;
		ghgIntensityShip: ValueUnit;
		ghgIntensityTarget: ValueUnit;
		complianceBalance: ValueUnit;
	};
	penalty: number;
};

export const FuelDataCalculationLabel = {
	CO2eqWtT: 'CO2eq WtT',
	LCV: 'LCV',
	RWD: 'RWD',
	CO2eqTtW: 'CO2eq TtW',
	Cf_CO2: 'Cf (CO2)',
	Cf_CH4: 'Cf (CH4)',
	Cf_N2O: 'Cf (N2O)',
	OneSubCslip_Mul_CO2eqTtW: '(1 - Cslip) * CO2eq TtW',
	CO2eqTtWslip: 'CO2eq TtW slip',
	Csf_CO2: 'Csf (CO2)',
	Csf_CH4: 'Csf (CH4)',
	Csf_N2O: 'Csf (N2O)',
	Cslip: 'Cslip',
	Cslip_MUL_CO2eqTtWslip: 'Cslip * CO2eq TtW slip',
	TtWtotalincl_slip: 'TtW total incl. slip',
	WellToTankShip: 'Well to tank ship',
	TankToWakeShip: 'Tank to wake ship',
	CO2eqemittedWtT: 'CO2eq emitted WtT',
	CO2eqemittedTtW: 'CO2eq emitted TtW',
	CO2eqemittedWtW: 'CO2eq emitted WtW',
	EnergyUsedOnBoard: 'Energy used on board',
	GhgIntensityShip: 'GHG Intensity ship',
	GhgIntensityTarget: 'GHG Intensity target',
	ComplianceBalance: 'Compliance Balance',
} as const;

export const calculateFuelEuData = (
	id: number,
	estimateId: number,
	name: string,
	fuelType: Values<typeof FuelTypes>,
	consumption: number,
	year: number,
): FuelEuDataCalculation => {
	const fuelData = FuelEuData.find((fd) => fd.pathwayName === fuelType);
	const fuelPenalties = FuelEuPenlaties.find((fp) => fp.year === year);

	if (!fuelData || !fuelPenalties) {
		// Return default zeros if data not found
		return {
			id,
			estimateId,
			name,
			fuelType,
			consumption,
			year,
			wellToTank: {
				co2eqWtt: { label: FuelDataCalculationLabel.CO2eqWtT,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_MJ },
				lcv: { label: FuelDataCalculationLabel.LCV,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.MJ_PER_G_FUEL },
				rwd: { label: FuelDataCalculationLabel.RWD, value: 0, displayValue: '0', unit: UnitTypes.NONE },
			},
			tankToWake: {
				co2eqTtw: { label: FuelDataCalculationLabel.CO2eqTtW,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
				cfCo2: { label: FuelDataCalculationLabel.Cf_CO2,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_PER_G_FUEL },
				cfCh4: { label: FuelDataCalculationLabel.Cf_CH4,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CH4_PER_G_FUEL },
				cfN2o: { label: FuelDataCalculationLabel.Cf_N2O,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_N2O_PER_G_FUEL },
				slipFactor: { label: FuelDataCalculationLabel.OneSubCslip_Mul_CO2eqTtW,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
			},
			cSlip: {
				co2eqTtwSlip: { label: FuelDataCalculationLabel.CO2eqTtWslip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
				csfCo2: { label: FuelDataCalculationLabel.Csf_CO2,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_PER_G_FUEL },
				csfCh4: { label: FuelDataCalculationLabel.Csf_CH4,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CH4_PER_G_FUEL },
				csfN2o: { label: FuelDataCalculationLabel.Csf_N2O,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_N2O_PER_G_FUEL },
				cSlip: { label: FuelDataCalculationLabel.Cslip, value: 0, displayValue: '0', unit: UnitTypes.PERCENT },
				cSlipCo2eqTtwSlip: { label: FuelDataCalculationLabel.Cslip_MUL_CO2eqTtWslip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
				ttwTotalInclSlip: { label: FuelDataCalculationLabel.TtWtotalincl_slip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
			},
			parameters: {
				wellToTankShip: { label: FuelDataCalculationLabel.WellToTankShip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_MJ },
				tankToWakeShip: { label: FuelDataCalculationLabel.TankToWakeShip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_MJ },
				co2eqEmittedWtT: { label: FuelDataCalculationLabel.CO2eqemittedWtT,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ },
				co2eqEmittedTtW: { label: FuelDataCalculationLabel.CO2eqemittedTtW,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ },
				co2eqEmittedWtW: { label: FuelDataCalculationLabel.CO2eqemittedWtW,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ },
				energyUsedOnBoard: { label: FuelDataCalculationLabel.EnergyUsedOnBoard,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.MJ },
				ghgIntensityShip: { label: FuelDataCalculationLabel.GhgIntensityShip,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_MJ },
				ghgIntensityTarget: { label: FuelDataCalculationLabel.GhgIntensityTarget,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ_PER_MJ },
				complianceBalance: { label: FuelDataCalculationLabel.ComplianceBalance,
					value: 0,
					displayValue: '0',
					unit: UnitTypes.G_CO2_EQ },
			},
			penalty: 0,
		};
	}

	const {
		co2eqWtt = 0,
		cfCo2 = 1,
		cfCh4 = 1,
		cfN2o = 1,
		cslip = 0,
		lcv = 0,
		fuelClass,
	} = fuelData;

	const rwd = fuelClass === FuelClass.RFNBO ? 2 : 1;
	const cO2eqTtW = cfCo2 * GWP_CO2 + cfCh4 * GWP_CH4 + cfN2o * GWP_N2O;
	const slipFactor = (1 - 0.01 * cslip) * cO2eqTtW;

	const wellToTankShip = rwd > 0 ? co2eqWtt / rwd : 0;
	const tankToWakeShip = (slipFactor + 0.01 * cslip * CO2EQ_TTW_SLIP) / (lcv * rwd || 1);

	const ghgIntensityShip = wellToTankShip + tankToWakeShip;
	const energyUsedOnBoard = consumption * 1_000_000 * lcv;
	const { ghgIntensityTarget } = fuelPenalties;

	const complianceBalance = (ghgIntensityTarget - ghgIntensityShip) * energyUsedOnBoard;

	const penalty = complianceBalance > 0 ?
		0 :
		(Math.abs(complianceBalance) * ENERGY_PER_TONNE_OF_FUEL * fuelPenalties.penaltyMultiplier) /
          (ghgIntensityShip * EMISSION_NORMALIZATION_FACTOR || 1);

	return {
		id,
		estimateId,
		name,
		fuelType,
		consumption,
		year,
		wellToTank: {
			co2eqWtt: { label: FuelDataCalculationLabel.CO2eqWtT,
				value: co2eqWtt,
				displayValue: formatNumber(co2eqWtt, { separateThousands: true }, 2),
				unit: UnitTypes.G_CO2_EQ_PER_MJ },
			lcv: { label: FuelDataCalculationLabel.LCV,
				value: lcv,
				displayValue: formatNumber(lcv, { separateThousands: true }, 4),
				unit: UnitTypes.MJ_PER_G_FUEL },
			rwd: { label: FuelDataCalculationLabel.RWD,
				value: rwd,
				displayValue: formatNumber(rwd, { integersOnly: true }),
				unit: UnitTypes.NONE },
		},
		tankToWake: {
			co2eqTtw: { label: FuelDataCalculationLabel.CO2eqTtW,
				value: cO2eqTtW,
				displayValue: formatNumber(cO2eqTtW, { separateThousands: true }, 4),
				unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
			cfCo2: { label: FuelDataCalculationLabel.Cf_CO2,
				value: cfCo2,
				displayValue: formatNumber(cfCo2, { separateThousands: true }, 3),
				unit: UnitTypes.G_CO2_PER_G_FUEL },
			cfCh4: { label: FuelDataCalculationLabel.Cf_CH4,
				value: cfCh4,
				displayValue: formatNumber(cfCh4, { separateThousands: true }, 5),
				unit: UnitTypes.G_CH4_PER_G_FUEL },
			cfN2o: { label: FuelDataCalculationLabel.Cf_N2O,
				value: cfN2o,
				displayValue: formatNumber(cfN2o, { separateThousands: true }, 5),
				unit: UnitTypes.G_N2O_PER_G_FUEL },
			slipFactor: { label: FuelDataCalculationLabel.OneSubCslip_Mul_CO2eqTtW,
				value: slipFactor,
				displayValue: formatNumber(slipFactor, { separateThousands: true }, 4),
				unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
		},
		cSlip: {
			co2eqTtwSlip: { label: FuelDataCalculationLabel.CO2eqTtWslip,
				value: CO2EQ_TTW_SLIP,
				displayValue: formatNumber(CO2EQ_TTW_SLIP, { integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
			csfCo2: { label: FuelDataCalculationLabel.Csf_CO2,
				value: CSF_CO2_SLIP,
				displayValue: formatNumber(CSF_CO2_SLIP, { integersOnly: true }),
				unit: UnitTypes.G_CO2_PER_G_FUEL },
			csfCh4: { label: FuelDataCalculationLabel.Csf_CH4,
				value: CSF_CH4_SLIP,
				displayValue: formatNumber(CSF_CH4_SLIP, { integersOnly: true }),
				unit: UnitTypes.G_CH4_PER_G_FUEL },
			csfN2o: { label: FuelDataCalculationLabel.Csf_N2O,
				value: CSF_N2O_SLIP,
				displayValue: formatNumber(CSF_N2O_SLIP, { integersOnly: true }),
				unit: UnitTypes.G_N2O_PER_G_FUEL },
			cSlip: { label: FuelDataCalculationLabel.Cslip,
				value: cslip,
				displayValue: formatNumber(cslip, { integersOnly: true }),
				unit: UnitTypes.PERCENT },
			cSlipCo2eqTtwSlip: { label: FuelDataCalculationLabel.Cslip_MUL_CO2eqTtWslip,
				value: C_SLIP_MULTIPLIED_CO2EQ_TTW_SLIP,
				displayValue: formatNumber(C_SLIP_MULTIPLIED_CO2EQ_TTW_SLIP, { integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
			ttwTotalInclSlip: { label: FuelDataCalculationLabel.TtWtotalincl_slip,
				value: cslip + slipFactor,
				displayValue: formatNumber(cslip + slipFactor, { integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ_PER_G_FUEL },
		},
		parameters: {
			wellToTankShip: { label: FuelDataCalculationLabel.WellToTankShip,
				value: wellToTankShip,
				displayValue: formatNumber(wellToTankShip, { separateThousands: true }, 1),
				unit: UnitTypes.G_CO2_EQ_PER_MJ },
			tankToWakeShip: { label: FuelDataCalculationLabel.TankToWakeShip,
				value: tankToWakeShip,
				displayValue: formatNumber(tankToWakeShip, { separateThousands: true }, 1),
				unit: UnitTypes.G_CO2_EQ_PER_MJ },
			co2eqEmittedWtT: { label: FuelDataCalculationLabel.CO2eqemittedWtT,
				value: energyUsedOnBoard * wellToTankShip,
				displayValue:
				formatNumber(energyUsedOnBoard * wellToTankShip, { separateThousands: true, integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ },
			co2eqEmittedTtW: { label: FuelDataCalculationLabel.CO2eqemittedTtW,
				value: energyUsedOnBoard * tankToWakeShip,
				displayValue:
				formatNumber(energyUsedOnBoard * tankToWakeShip, { separateThousands: true, integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ },
			co2eqEmittedWtW: { label: FuelDataCalculationLabel.CO2eqemittedWtW,
				value: energyUsedOnBoard * ghgIntensityShip,
				displayValue:
				formatNumber(energyUsedOnBoard * ghgIntensityShip, { separateThousands: true, integersOnly: true }),
				unit: UnitTypes.G_CO2_EQ },
			energyUsedOnBoard: { label: FuelDataCalculationLabel.EnergyUsedOnBoard,
				value: energyUsedOnBoard,
				displayValue: formatNumber(energyUsedOnBoard, { separateThousands: true, integersOnly: true }),
				unit: UnitTypes.MJ },
			ghgIntensityShip: { label: FuelDataCalculationLabel.GhgIntensityShip,
				value: ghgIntensityShip,
				displayValue: formatNumber(ghgIntensityShip, { separateThousands: true }, 1),
				unit: UnitTypes.G_CO2_EQ_PER_MJ },
			ghgIntensityTarget: { label: FuelDataCalculationLabel.GhgIntensityTarget,
				value: ghgIntensityTarget,
				displayValue: formatNumber(ghgIntensityTarget, { separateThousands: true }, 1),
				unit: UnitTypes.G_CO2_EQ_PER_MJ },
			complianceBalance: { label: FuelDataCalculationLabel.ComplianceBalance,
				value: complianceBalance,
				displayValue: formatNumber(complianceBalance, { separateThousands: true }, 2),
				unit: UnitTypes.G_CO2_EQ },
		},
		penalty,
	};
};

const toValueUnit = (label: Values<typeof FuelDataCalculationLabel>,
	value: number, displayValue: string | null, unit: Values<typeof UnitTypes>): ValueUnit => {
	return { label, value, displayValue, unit };
};

export const transformFuelEuToCalculation = (entry: FuelEuProps): FuelEuDataCalculation => {
	const result = {
		id: entry.id,
		estimateId: entry.estimateId,
		name: entry.name,
		fuelType: entry.fuelType,
		consumption: entry.consumption,
		year: entry.year,
		wellToTank: {
			co2eqWtt: toValueUnit(FuelDataCalculationLabel.CO2eqWtT, entry.co2eqWtt,
				formatNumber(entry.co2eqWtt, { separateThousands: true }, 2),
				UnitTypes.G_CO2_EQ_PER_MJ),
			lcv: toValueUnit(FuelDataCalculationLabel.LCV, entry.lcv,
				formatNumber(entry.lcv, { separateThousands: true }, 4), UnitTypes.MJ_PER_G_FUEL),
			rwd: toValueUnit(FuelDataCalculationLabel.RWD,
				entry.rwd, formatNumber(entry.rwd, { integersOnly: true }), UnitTypes.NONE),
		},
		tankToWake: {
			co2eqTtw: toValueUnit(FuelDataCalculationLabel.CO2eqTtW, entry.co2eqTtw,
				formatNumber(entry.co2eqTtw, { separateThousands: true }, 4),
				UnitTypes.G_CO2_EQ_PER_G_FUEL),
			cfCo2: toValueUnit(FuelDataCalculationLabel.Cf_CO2, entry.cfCo2,
				formatNumber(entry.cfCo2, { separateThousands: true }, 3),
				UnitTypes.G_CO2_PER_G_FUEL),
			cfCh4: toValueUnit(FuelDataCalculationLabel.Cf_CH4, entry.cfCh4,
				formatNumber(entry.cfCh4, { separateThousands: true }, 5),
				UnitTypes.G_CH4_PER_G_FUEL),
			cfN2o: toValueUnit(FuelDataCalculationLabel.Cf_N2O, entry.cfN2o,
				formatNumber(entry.cfN2o, { separateThousands: true }, 5), UnitTypes.G_N2O_PER_G_FUEL),
			slipFactor: toValueUnit(FuelDataCalculationLabel.OneSubCslip_Mul_CO2eqTtW, entry.slipFactor,
				formatNumber(entry.slipFactor, { separateThousands: true }, 5), UnitTypes.G_CO2_EQ_PER_G_FUEL),
		},
		cSlip: {
			co2eqTtwSlip: toValueUnit(FuelDataCalculationLabel.CO2eqTtWslip, entry.co2eqTtwSlip,
				formatNumber(entry.co2eqTtwSlip, { integersOnly: true }), UnitTypes.G_CO2_EQ_PER_G_FUEL),
			csfCo2: toValueUnit(FuelDataCalculationLabel.Csf_CO2, entry.csfCo2,
				formatNumber(entry.csfCo2, { integersOnly: true }), UnitTypes.G_CO2_PER_G_FUEL),
			csfCh4: toValueUnit(FuelDataCalculationLabel.Csf_CH4, entry.csfCh4,
				formatNumber(entry.csfCh4, { integersOnly: true }), UnitTypes.G_CH4_PER_G_FUEL),
			csfN2o: toValueUnit(FuelDataCalculationLabel.Csf_N2O, entry.csfN2o,
				formatNumber(entry.csfN2o, { integersOnly: true }), UnitTypes.G_N2O_PER_G_FUEL),
			cSlip: toValueUnit(FuelDataCalculationLabel.Cslip, entry.cSlip,
				formatNumber(entry.cSlip, { integersOnly: true }), UnitTypes.PERCENT),
			cSlipCo2eqTtwSlip: toValueUnit(FuelDataCalculationLabel.Cslip_MUL_CO2eqTtWslip, entry.cSlipCo2eqTtwSlip,
				formatNumber(entry.cSlipCo2eqTtwSlip, { integersOnly: true }), UnitTypes.G_CO2_EQ_PER_G_FUEL),
			ttwTotalInclSlip: toValueUnit(FuelDataCalculationLabel.TtWtotalincl_slip, entry.ttwTotalInclSlip,
				formatNumber(entry.ttwTotalInclSlip, { separateThousands: true }, 4), UnitTypes.G_CO2_EQ_PER_G_FUEL),
		},
		parameters: {
			wellToTankShip: toValueUnit(FuelDataCalculationLabel.WellToTankShip, entry.wellToTankShip,
				formatNumber(entry.wellToTankShip, { separateThousands: true }, 1), UnitTypes.G_CO2_EQ_PER_MJ),
			tankToWakeShip: toValueUnit(FuelDataCalculationLabel.TankToWakeShip, entry.tankToWakeShip,
				formatNumber(entry.tankToWakeShip, { separateThousands: true }, 1), UnitTypes.G_CO2_EQ_PER_MJ),
			co2eqEmittedWtT: toValueUnit(FuelDataCalculationLabel.CO2eqemittedWtT, entry.co2eqEmittedWtT,
				formatNumber(entry.co2eqEmittedWtT,
					{ integersOnly: true, separateThousands: true }), UnitTypes.G_CO2_EQ),
			co2eqEmittedTtW: toValueUnit(FuelDataCalculationLabel.CO2eqemittedTtW, entry.co2eqEmittedTtW,
				formatNumber(entry.co2eqEmittedTtW,
					{ integersOnly: true, separateThousands: true }), UnitTypes.G_CO2_EQ),
			co2eqEmittedWtW: toValueUnit(FuelDataCalculationLabel.CO2eqemittedWtW, entry.co2eqEmittedWtW,
				formatNumber(entry.co2eqEmittedWtW,
					{ integersOnly: true, separateThousands: true }), UnitTypes.G_CO2_EQ),
			energyUsedOnBoard: toValueUnit(FuelDataCalculationLabel.EnergyUsedOnBoard, entry.energyUsedOnBoard,
				formatNumber(entry.energyUsedOnBoard, { integersOnly: true }), UnitTypes.MJ),
			ghgIntensityShip: toValueUnit(FuelDataCalculationLabel.GhgIntensityShip, entry.ghgIntensityShip,
				formatNumber(entry.ghgIntensityShip, { separateThousands: true }, 1), UnitTypes.G_CO2_EQ_PER_MJ),
			ghgIntensityTarget: toValueUnit(FuelDataCalculationLabel.GhgIntensityTarget, entry.ghgIntensityTarget,
				formatNumber(entry.ghgIntensityTarget, { separateThousands: true }, 1), UnitTypes.G_CO2_EQ_PER_MJ),
			complianceBalance: toValueUnit(FuelDataCalculationLabel.ComplianceBalance, entry.complianceBalance,
				formatNumber(entry.complianceBalance, { separateThousands: true }, 2), UnitTypes.G_CO2_EQ),
		},
		penalty: entry.penalty,
	};

	return result;
};

export const transformCalculationToFuelEu = (data: FuelEuDataCalculation): FuelEuProps => {
	return {
		id: data.id,
		estimateId: data.estimateId,
		name: data.name,
		fuelType: data.fuelType,
		consumption: data.consumption,
		year: data.year,

		// wellToTank
		co2eqWtt: data.wellToTank.co2eqWtt.value,
		lcv: data.wellToTank.lcv.value,
		rwd: data.wellToTank.rwd.value,

		// tankToWake
		co2eqTtw: data.tankToWake.co2eqTtw.value,
		cfCo2: data.tankToWake.cfCo2.value,
		cfCh4: data.tankToWake.cfCh4.value,
		cfN2o: data.tankToWake.cfN2o.value,
		slipFactor: data.tankToWake.slipFactor.value,

		// cSlip
		co2eqTtwSlip: data.cSlip.co2eqTtwSlip.value,
		csfCo2: data.cSlip.csfCo2.value,
		csfCh4: data.cSlip.csfCh4.value,
		csfN2o: data.cSlip.csfN2o.value,
		cSlip: data.cSlip.cSlip.value,
		cSlipCo2eqTtwSlip: data.cSlip.cSlipCo2eqTtwSlip.value,
		ttwTotalInclSlip: data.cSlip.ttwTotalInclSlip.value,

		// parameters
		wellToTankShip: data.parameters.wellToTankShip.value,
		tankToWakeShip: data.parameters.tankToWakeShip.value,
		co2eqEmittedWtT: data.parameters.co2eqEmittedWtT.value,
		co2eqEmittedTtW: data.parameters.co2eqEmittedTtW.value,
		co2eqEmittedWtW: data.parameters.co2eqEmittedWtW.value,
		energyUsedOnBoard: data.parameters.energyUsedOnBoard.value,
		ghgIntensityShip: data.parameters.ghgIntensityShip.value,
		ghgIntensityTarget: data.parameters.ghgIntensityTarget.value,
		complianceBalance: data.parameters.complianceBalance.value,

		// penalty
		penalty: data.penalty,
	} as FuelEuProps;
};

export const yearOptions = Array.from({ length: 26 }, (_, i) => {
	const year = new Date().getFullYear() + i;

	return { label: year.toString(), value: year };
});
